/**
 * Mobile Portal Sidebar
 *
 * Purpose: Nav for field ops (per PRD §6): mobile units, ATMs, activity. Not agents.
 * Location: mobile-portal/src/components/layout/Sidebar.tsx
 */

import { cn } from '@smartpay/utils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Truck,
  Banknote,
  MapPin,
  Send,
  ClipboardList,
  Settings,
  HelpCircle,
  ChevronLeft,
  LogOut,
  User,
  Menu,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/' },
  { label: 'Field Hub', icon: Truck, href: '/field' },
  { label: 'ATMs', icon: Banknote, href: '/atms' },
];

const bottomNavItems: NavItem[] = [
  { label: 'Settings', icon: Settings, href: '/settings' },
  { label: 'Help', icon: HelpCircle, href: '/help' },
];

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

export function Sidebar({ isMobileOpen: controlledMobileOpen, onMobileOpenChange }: SidebarProps = {}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const isMobileOpen = controlledMobileOpen ?? internalMobileOpen;
  const setIsMobileOpen = onMobileOpenChange ?? setInternalMobileOpen;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeItem = location.pathname;

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive =
      activeItem === item.href ||
      (item.href !== '/' && item.href !== '#' && activeItem.startsWith(item.href + '/'));
    const Icon = item.icon;
    return (
      <motion.button
        onClick={() => navigate(item.href)}
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
          isActive
            ? 'bg-primary/10 text-primary shadow-sm border border-primary/20'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
      >
        {isActive && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary"
          />
        )}
        <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
        {(!isCollapsed || isMobileOpen) && <span className="flex-1 text-left">{item.label}</span>}
      </motion.button>
    );
  };

  const desktopSidebar = (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 72 : 260 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={cn(
        'hidden lg:flex fixed left-0 top-0 z-40 h-screen flex-col border-r bg-card',
        isCollapsed && 'items-center'
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-border bg-card px-4">
        {!isCollapsed ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3">
            <div className="ketchup-logo-wrap shrink-0">
              <img src="/ketchup-logo.png" alt="Ketchup SmartPay" className="h-9 w-9 rounded-lg object-contain" />
            </div>
            <div>
              <p className="font-display text-sm font-semibold tracking-tight text-foreground">Mobile Portal</p>
              <p className="text-[10px] text-muted-foreground">Ketchup SmartPay</p>
            </div>
          </motion.div>
        ) : (
          <div className="ketchup-logo-wrap mx-auto shrink-0">
            <img src="/ketchup-logo.png" alt="Mobile Portal" className="h-9 w-9 rounded-lg object-contain" />
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', isCollapsed && 'rotate-180')} />
        </button>
      </div>

      {!isCollapsed && user && (
        <div className="border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role === 'supervisor' ? 'Supervisor' : 'Field ops'}</p>
            </div>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navItems.map((item) => (
          <NavLink key={item.href} item={item} />
        ))}
      </nav>

      <div className="border-t p-3">
        <div className="space-y-1">
          {bottomNavItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
        </div>
        <div className="mt-3 border-t pt-3">
          <button
            onClick={logout}
            className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </motion.aside>
  );

  const mobileSidebar = (
    <AnimatePresence>
      {isMobileOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 z-50 h-screen w-[280px] flex-col border-r bg-card lg:hidden flex"
          >
            <div className="flex h-16 items-center justify-between border-b border-border bg-card px-4">
              <div className="flex items-center gap-3">
                <div className="ketchup-logo-wrap shrink-0">
                  <img src="/ketchup-logo.png" alt="Mobile Portal" className="h-9 w-9 rounded-lg object-contain" />
                </div>
                <div>
                  <p className="font-display text-sm font-semibold tracking-tight text-foreground">Mobile Portal</p>
                  <p className="text-[10px] text-muted-foreground">Ketchup SmartPay</p>
                </div>
              </div>
              <button
                onClick={() => setIsMobileOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {user && (
              <div className="border-b border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user.role === 'supervisor' ? 'Supervisor' : 'Field ops'}</p>
                  </div>
                </div>
              </div>
            )}

            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {navItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </nav>

            <div className="border-t p-3">
              <div className="space-y-1">
                {bottomNavItems.map((item) => (
                  <NavLink key={item.href} item={item} />
                ))}
              </div>
              <div className="mt-3 border-t pt-3">
                <button
                  onClick={logout}
                  className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  const mobileToggleButton = (
    <button
      onClick={() => setIsMobileOpen(true)}
      className="fixed bottom-4 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg lg:hidden"
      aria-label="Open menu"
    >
      <Menu className="h-6 w-6" />
    </button>
  );

  return (
    <>
      {desktopSidebar}
      {mobileSidebar}
      {mobileToggleButton}
    </>
  );
}
